import React, { useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { InteractiveCard } from '@rentspree/component-2023.components.organisms.interactive-card';

import { UserRole, USER_ROLES } from '../../types/tenantScreening';
import {
  AgentIcon,
  LandlordIcon,
  RenterIcon,
  ManagerIcon,
} from '../atoms/icons';
import { MultiStepLayout } from '../templates/MultiStepLayout';

import { SectionHeader } from './SectionHeader';

export interface RoleSelectionStepProps {
  selectedRole?: UserRole | undefined;
  onRoleChange: (role: UserRole) => void;
  onNext: () => void;
  onBack: () => void;
  onHomeClick?: () => void;
  onExitClick?: () => void;
  isLoading?: boolean | undefined;
  // MultiStepLayout props - optional to allow content-only usage
  currentStep?: number;
  total?: number;
  currentSubStepPercentage?: number;
  title?: string;
  description?: string;
  // If true, renders only content without MultiStepLayout wrapper
  contentOnly?: boolean;
  // Hide the internal SectionHeader when using MultiStepLayout's title/description
  hideInternalHeader?: boolean;
}

export interface RoleSelectionContentProps {
  selectedRole?: UserRole | undefined;
  onRoleChange: (role: UserRole) => void;
  isLoading?: boolean | undefined;
  title?: string;
  description?: string;
  // Hide the internal SectionHeader when using MultiStepLayout's title/description
  hideInternalHeader?: boolean;
}

// Content-only component for reuse
export const RoleSelectionContent: React.FC<RoleSelectionContentProps> = ({
  selectedRole,
  onRoleChange,
  isLoading = false,
  title = "What's your role for this property?",
  description = 'This helps us set up the right tools for you.',
  hideInternalHeader = false,
}) => {
  const handleRoleSelect = (role: UserRole) => {
    onRoleChange(role);
  };

  const getRoleIcon = (iconName: string) => {
    const iconProps = {
      sx: {
        fontSize: { xs: 40, sm: 48, md: 56 },
        color: 'primary.main',
      },
    };

    switch (iconName) {
      case 'agent':
        return <AgentIcon {...iconProps} />;
      case 'landlord':
        return <LandlordIcon {...iconProps} />;
      case 'renter':
        return <RenterIcon {...iconProps} />;
      case 'manager':
        return <ManagerIcon {...iconProps} />;
      default:
        return <AgentIcon {...iconProps} />;
    }
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: 700, md: 800, lg: 900 },
        mx: 'auto',
        px: { xs: 1, sm: 2, md: 3 },
        width: '100%',
      }}
    >
      {!hideInternalHeader && (
        <SectionHeader title={title} description={description} align='center' />
      )}
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {USER_ROLES.map(roleInfo => (
          <Grid item xs={12} sm={6} key={roleInfo.role}>
            <InteractiveCard
              onClick={() => handleRoleSelect(roleInfo.role)}
              isSelected={selectedRole === roleInfo.role}
              disabled={isLoading}
              sx={{
                height: '100%',
                minHeight: { xs: 100, sm: 120, md: 140 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 1.5, sm: 2 },
                p: { xs: 2, sm: 3 },
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <Box
                sx={{
                  fontSize: '2rem',
                  mb: 1,
                }}
              >
                {getRoleIcon(roleInfo.icon)}
              </Box>

              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  mb: 1,
                }}
              >
                {roleInfo.label}
              </Typography>

              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  textAlign: 'center',
                  fontSize: '0.9rem',
                }}
              >
                {roleInfo.description}
              </Typography>
            </InteractiveCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Full step component with MultiStepLayout
export const RoleSelectionStep: React.FC<RoleSelectionStepProps> = ({
  selectedRole,
  onRoleChange,
  onNext,
  onBack,
  onHomeClick,
  onExitClick,
  isLoading = false,
  currentStep = 2,
  total = 7,
  currentSubStepPercentage = 100,
  title = "What's your role for this property?",
  description = 'This helps us set up the right tools for you.',
  contentOnly = false,
  hideInternalHeader = false,
}) => {
  const [localSelectedRole, setLocalSelectedRole] = useState<
    UserRole | undefined
  >(selectedRole);

  const handleRoleChange = (role: UserRole) => {
    setLocalSelectedRole(role);
    onRoleChange(role);
  };

  const handleNext = () => {
    if (localSelectedRole && !isLoading) {
      onNext();
    }
  };

  // If contentOnly, return just the content
  if (contentOnly) {
    return (
      <RoleSelectionContent
        selectedRole={localSelectedRole}
        onRoleChange={handleRoleChange}
        isLoading={isLoading}
        title={title}
        description={description}
        hideInternalHeader={hideInternalHeader}
      />
    );
  }

  // Return full step with MultiStepLayout
  return (
    <MultiStepLayout
      currentStep={currentStep}
      total={total}
      currentSubStepPercentage={currentSubStepPercentage}
      onBack={onBack}
      onNext={handleNext}
      onHomeClick={onHomeClick}
      onExitClick={onExitClick}
      backDisabled={isLoading}
      nextDisabled={!localSelectedRole || isLoading}
    >
      <RoleSelectionContent
        selectedRole={localSelectedRole}
        onRoleChange={handleRoleChange}
        isLoading={isLoading}
        title={title}
        description={description}
        hideInternalHeader={hideInternalHeader}
      />
    </MultiStepLayout>
  );
};

export default RoleSelectionStep;
